import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type { CalendarViewMode } from '@/types/calendar';
import { getTodayDateString } from '@/lib/dateOnly';

export type NavigationIntent = 'previous' | 'next' | 'today' | null;

interface CalendarViewState {
  activeView: CalendarViewMode;
  focusDate: string;
  navigationIntent: NavigationIntent;
  searchQuery: string;
  selectedEventId: string | null;
  highlightedEventId: string | null;
  isSidebarOpen: boolean;
  eventDetailAnchor: { x: number; y: number; width: number; height: number } | null;
  setActiveView: (view: CalendarViewMode) => void;
  setFocusDate: (date: string, intent?: NavigationIntent) => void;
  clearNavigationIntent: () => void;
  setSearchQuery: (query: string) => void;
  setSelectedEventId: (eventId: string | null) => void;
  setHighlightedEventId: (eventId: string | null) => void;
  setSidebarOpen: (open: boolean) => void;
  setEventDetailAnchor: (rect: { x: number; y: number; width: number; height: number } | null) => void;
  openEventDetail: (eventId: string, rect?: DOMRect | null) => void;
  closeEventDetail: () => void;
  resetFocusDate: () => void;
}

export const useCalendarViewStore = create<CalendarViewState>()(
  conditionalDevtools(
    persist(
      (set) => ({
        activeView: 'week',
        focusDate: getTodayDateString(),
        navigationIntent: null,
        searchQuery: '',
        selectedEventId: null,
        highlightedEventId: null,
        isSidebarOpen: false,
        eventDetailAnchor: null,
        setActiveView: (view) => set({ activeView: view }),
        setFocusDate: (date, intent) => set({ focusDate: date, navigationIntent: intent ?? null }),
        clearNavigationIntent: () => set({ navigationIntent: null }),
        setSearchQuery: (query) => set({ searchQuery: query }),
        setSelectedEventId: (eventId) => set({ selectedEventId: eventId }),
        setHighlightedEventId: (eventId) => set({ highlightedEventId: eventId }),
        setSidebarOpen: (open) => set({ isSidebarOpen: open }),
        setEventDetailAnchor: (rect) => set({ eventDetailAnchor: rect }),
        openEventDetail: (eventId, rect) =>
          set({
            selectedEventId: eventId,
            eventDetailAnchor: rect
              ? { x: rect.left, y: rect.top, width: rect.width, height: rect.height }
              : null,
          }),
        closeEventDetail: () => set({ selectedEventId: null, eventDetailAnchor: null }),
        resetFocusDate: () => set({ focusDate: getTodayDateString() }),
      }),
      {
        name: 'calendar-view-storage',
        partialize: (state) => ({
          activeView: state.activeView,
          focusDate: state.focusDate,
        }),
      }
    ),
    { name: 'CalendarViewStore' }
  )
);
